// frontend/src/context/AuthContext.js

import { createContext, useContext, useState, useEffect } from 'react';
import { 
    onAuthStateChanged, 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword 
} from 'firebase/auth';
import { auth } from '../config/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import authService from '../services/authService';

// Definición de roles y permisos como constantes
export const ROLES = {
    SUPER_ADMIN: 'super_admin',
    ADMINISTRADOR: 'administrador',
    AUDITOR_INTERIOR: 'auditor_interior',
    AUDITOR_CAPITAL: 'auditor_capital',
    AUDITOR_BSAS: 'auditor_bsas',
    AUDITOR_GENERAL: 'auditor_general', // ✨ auditor que abarca capital e interior
    AUDITOR_MEDICO_GENERAL: 'auditor_medico_general',
    SOLO_DERIVACIONES: 'solo_derivaciones',
    SOLO_CONVENIOS: 'solo_convenios',
    DERIVACIONES_Y_CONVENIOS: 'derivaciones_y_convenios',
    DERIVACIONES_INTERIOR: 'derivaciones_interior', // ✨ Nuevo perfil
    OPERADOR: 'operador',
    OBSERVADOR: 'observador', 
};

export const PERMISSIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  MANAGE_USERS: 'manage_users',
  VIEW_REPORTS: 'view_reports',
  READ_DERIVACIONES: 'read_derivaciones',
  READ_CONVENIOS: 'read_convenios'
};

const ROLE_ALIASES = {
  admin: ROLES.ADMINISTRADOR,
  auditor: ROLES.AUDITOR_GENERAL,
  solo_tramites: ROLES.SOLO_DERIVACIONES,
  solo_gestiones: ROLES.SOLO_CONVENIOS,
};

const normalizeRole = (role) => ROLE_ALIASES[role] || role || ROLES.OPERADOR;

const normalizePermissions = (rawPermissions = [], role) => {
  const normalizedRole = normalizeRole(role);
  const source = Array.isArray(rawPermissions) ? rawPermissions : [];
  const permissions = new Set(source);

  if (permissions.has('edit')) {
    permissions.add(PERMISSIONS.UPDATE);
  }

  if (permissions.has('*') || normalizedRole === ROLES.SUPER_ADMIN) {
    return ['*'];
  }

  const defaults = DEFAULT_PERMISSIONS[normalizedRole] || [];
  defaults.forEach((permission) => permissions.add(permission));

  return Array.from(permissions);
};

const DEFAULT_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: ['*'],
  [ROLES.ADMINISTRADOR]: [
    PERMISSIONS.CREATE,
    PERMISSIONS.READ,
    PERMISSIONS.UPDATE,
    PERMISSIONS.DELETE,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.AUDITOR_INTERIOR]: [
    PERMISSIONS.READ,
    PERMISSIONS.CREATE,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.AUDITOR_CAPITAL]: [
    PERMISSIONS.READ,
    PERMISSIONS.CREATE,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.AUDITOR_BSAS]: [
    PERMISSIONS.READ,
    PERMISSIONS.CREATE,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.AUDITOR_GENERAL]: [
    PERMISSIONS.READ,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.AUDITOR_MEDICO_GENERAL]: [
    PERMISSIONS.READ,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.SOLO_DERIVACIONES]: [
    PERMISSIONS.READ,
    PERMISSIONS.READ_DERIVACIONES,
    PERMISSIONS.CREATE,
    PERMISSIONS.UPDATE,
    PERMISSIONS.DELETE
  ],
  [ROLES.SOLO_CONVENIOS]: [
    PERMISSIONS.READ,
    PERMISSIONS.READ_CONVENIOS,
    PERMISSIONS.CREATE,
    PERMISSIONS.UPDATE,
    PERMISSIONS.DELETE
  ],
  [ROLES.DERIVACIONES_Y_CONVENIOS]: [
    PERMISSIONS.READ,
    PERMISSIONS.CREATE,
    PERMISSIONS.UPDATE,
    PERMISSIONS.DELETE
],
[ROLES.OPERADOR]: [
    PERMISSIONS.READ,
    PERMISSIONS.CREATE
  ],

[ROLES.OBSERVADOR]: [
    PERMISSIONS.READ,
    PERMISSIONS.READ_DERIVACIONES,
    PERMISSIONS.READ_CONVENIOS,
    PERMISSIONS.VIEW_REPORTS
  ],


};

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [permissions, setPermissions] = useState([]);

    const resolveUserData = async (authUser) => {
        const uidRef = doc(db, 'users', authUser.uid);
        const uidDoc = await getDoc(uidRef);

        if (uidDoc.exists()) {
            const userData = uidDoc.data();
            const normalizedRole = normalizeRole(userData.role);
            return {
                ref: uidRef,
                data: {
                    ...userData,
                    email: userData.email || authUser.email,
                    role: normalizedRole,
                    permissions: normalizePermissions(userData.permissions, normalizedRole),
                }
            };
        }

        const emailKey = authUser.email?.toLowerCase();
        if (!emailKey) return null;

        const emailRef = doc(db, 'users', emailKey);
        const emailDoc = await getDoc(emailRef);

        if (!emailDoc.exists()) return null;

        const legacyUserData = emailDoc.data();
        const normalizedRole = normalizeRole(legacyUserData.role);
        const normalizedData = {
            ...legacyUserData,
            email: legacyUserData.email || authUser.email,
            role: normalizedRole,
            permissions: normalizePermissions(legacyUserData.permissions, normalizedRole),
            migratedFromEmailKey: true,
            legacyEmailKey: emailKey,
        };

        await setDoc(uidRef, normalizedData, { merge: true });

        return {
            ref: uidRef,
            data: normalizedData,
        };
    };

    const signup = async (email, password) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const newUserData = {
                email,
                role: ROLES.OPERADOR,
                permissions: DEFAULT_PERMISSIONS[ROLES.OPERADOR],
                createdAt: new Date(),
                lastLogin: new Date(),
                status: 'active'
            };

            await setDoc(doc(db, 'users', userCredential.user.uid), newUserData);
            await authService.refreshToken();
            
            return userCredential.user;
        } catch (error) {
            console.error('Error en signup:', error);
            throw error;
        }
    };

    const login = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const resolvedUser = await resolveUserData(userCredential.user);

            if (!resolvedUser) {
                await authService.logout();
                throw new Error("Acceso denegado: su cuenta no está registrada. Contacte al administrador.");
            }

            const userData = resolvedUser.data;
            if (userData.status !== "active") {
                await authService.logout();
                throw new Error("Acceso denegado: su cuenta no está activa.");
            }

            await setDoc(
                resolvedUser.ref,
                { lastLogin: new Date() },
                { merge: true }
            );
    
            await authService.startTokenRefresh();
            return userCredential.user;
        } catch (error) {
            console.error('Error en login:', error);
            throw error;
        }
    };
    
    const logout = async () => {
        try {
            await authService.logout();
            setUser(null);
            setUserRole(null);
            setPermissions([]);
        } catch (error) {
            console.error('Error en logout:', error);
            throw error;
        }
    };

    const hasPermission = (permission) => {
        if (!user) return false;
        if (userRole === ROLES.SUPER_ADMIN) return true;
        if (userRole === ROLES.ADMINISTRADOR) return true;
        if (permissions.includes('*')) return true;
        if (permission === PERMISSIONS.UPDATE) {
            return permissions.includes(PERMISSIONS.UPDATE) || permissions.includes('edit');
        }
        return permissions.includes(permission);
    };

    const hasPermissions = (requiredPermissions) => {
        if (!user) return false;
        if (userRole === ROLES.SUPER_ADMIN) return true;
        if (userRole === ROLES.ADMINISTRADOR) return true;
        if (permissions.includes('*')) return true;
        if (!requiredPermissions || requiredPermissions.length === 0) return true;
        return requiredPermissions.every(permission => permissions.includes(permission));
    };

    const hasRole = (requiredRole) => {
        if (!user) return false;
        if (userRole === ROLES.SUPER_ADMIN) return true;
        if (Array.isArray(requiredRole)) {
            return requiredRole.includes(userRole);
        }
        return userRole === requiredRole;
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    console.log('Usuario autenticado:', user.uid);
                    await authService.refreshToken();

                    const resolvedUser = await resolveUserData(user);

                    if (resolvedUser) {
                        const userData = resolvedUser.data;
                        console.log('Datos del usuario:', userData);
                        
                        const normalizedRole = normalizeRole(userData.role);
                        const normalizedPermissions = normalizePermissions(userData.permissions, normalizedRole);

                        setUserRole(normalizedRole);
                        setPermissions(normalizedPermissions);
                        setUser({
                            ...user,
                            role: normalizedRole,
                            permissions: normalizedPermissions
                        });
                    } else {
                        const newUserData = {
                            email: user.email,
                            role: ROLES.OPERADOR,
                            permissions: DEFAULT_PERMISSIONS[ROLES.OPERADOR],
                            createdAt: new Date(),
                            lastLogin: new Date(),
                            status: 'active'
                        };
                        
                        await setDoc(doc(db, 'users', user.uid), newUserData);
                        setUserRole(ROLES.OPERADOR);
                        setPermissions(DEFAULT_PERMISSIONS[ROLES.OPERADOR]);
                        setUser({
                            ...user,
                            role: ROLES.OPERADOR,
                            permissions: DEFAULT_PERMISSIONS[ROLES.OPERADOR]
                        });
                    }
                } else {
                    console.log('No hay usuario autenticado');
                    setUser(null);
                    setUserRole(null);
                    setPermissions([]);
                }
            } catch (error) {
                console.error('Error al cargar datos del usuario:', error);
                setUser(null);
                setUserRole(null);
                setPermissions([]);
            } finally {
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const contextValue = {
        user,
        loading,
        userRole,
        permissions,
        login,
        logout,
        signup,
        hasPermission,
        hasPermissions,
        hasRole,
        isSuperAdmin: userRole === ROLES.SUPER_ADMIN,
        isAdmin: userRole === ROLES.ADMINISTRADOR || userRole === ROLES.SUPER_ADMIN,
        isObserver: userRole === ROLES.OBSERVADOR,
        isAuditor: [
            ROLES.AUDITOR_INTERIOR,
            ROLES.AUDITOR_CAPITAL,
            ROLES.AUDITOR_BSAS,
            ROLES.AUDITOR_GENERAL,
            ROLES.AUDITOR_MEDICO_GENERAL
        ].includes(userRole),
        isOperator: userRole === ROLES.OPERADOR,
        ROLES,
        PERMISSIONS
    };
    

    return (
        <AuthContext.Provider value={contextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};

export default AuthContext;
